import React, { useMemo } from "react";
import { Select, SelectProps, Group, Text, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { TIRE_SET_STATUS_OPTIONS, TIRE_SET_STATUS_MAP } from "@/config/tireOptions";

import { TIRE_SET_STATUS } from "@/utils/api/TireSetService";

// ============================================
// Types
// ============================================

export interface TireSetStatusSelectorProps
  extends Omit<SelectProps, "data" | "value" | "onChange"> {
  /** Selected status value */
  value?: string | null;

  /** Callback when status changes */
  onChange?: (name: string | undefined, value: string | null | undefined) => void;
}

// ============================================
// Component
// ============================================

export const TireSetStatusSelector: React.FC<TireSetStatusSelectorProps> = ({
  name,
  value,
  onChange,
  placeholder = "Select status",
  label = "Status",
  required = false,
  ...rest
}) => {
  const { t } = useTranslation(["tireSets"]);

  // Convert options to Select data format
  const options = useMemo(() => {
    return TIRE_SET_STATUS_OPTIONS.map((opt) => ({
      value: '' + opt.value,
      label: t(`tireSets:${opt.labelKey}`),
    }));
  }, [t]);

  // Handle change with proper typing
  const handleChange = (newValue: string | null) => {
    onChange?.(name, newValue);
  };

  // Render option with status dot
  const renderOption: SelectProps["renderOption"] = ({ option }) => {
    const statusOpt = TIRE_SET_STATUS_MAP[option.value];
    if (!statusOpt) return option.label;

    return (
      <Group gap="sm" wrap="nowrap">
        <Box
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: `var(--mantine-color-${statusOpt.color}-5)`,
            boxShadow:
              Number(option.value) === TIRE_SET_STATUS.ACTIVE
                ? `0 0 6px var(--mantine-color-${statusOpt.color}-3)`
                : undefined,
            flexShrink: 0,
          }}
        />
        <Text size="sm">{t(`tireSets:${statusOpt.labelKey}`)}</Text>
      </Group>
    );
  };

  return (
    <Select
      data={options}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      label={label}
      clearable={!required}
      withAsterisk={required}
      renderOption={renderOption}
      allowDeselect={false}
      maxDropdownHeight={300}
      {...rest}
    />
  );
};
